import { FC, ReactNode, useCallback } from 'react'

import { useAppSelector } from '../../../../hooks/storeHooks'
import { useRouteModal } from '../../../route-modals/hooks/useRouteModal'
import { EModalType } from '../../../route-modals/types/modalTypes'
import { EventDialogTab } from '../../hooks/useEventDialogTabs'
import { useTrackedGames } from '../../hooks/useTrackedGames'
import { useDefaultLiveEvents } from '../../hooks/useTrackedGamesEvents'
import { selectHighlightedEvent } from '../../slices/liveEventModalSlice'
import { TrackedGame } from '../../types/TrackedGame'
import { TrackingEvent } from '../../types/TrackingEvents'
import { LiveEventsEventDialog } from '../LiveEventsEventDialog/LiveEventsEventDialog'

/**
 * Parameters for the LiveEventsEventTypeDialog modal
 */
export type LiveEventsEventTypeDialogParams = {
  /** The ID of the live event type to display */
  liveEventTypeId: string
  /** The ID of the tracked game associated with the event type */
  trackedGameId: string
  /** The active tab to display in the dialog */
  tab: EventDialogTab
  /** Optional: The ID of a specific event (used for Evolution tab to load archetype data) */
  liveEventId?: string
}

/**
 * Props for the LiveEventsEventTypeDialogContainer component
 */
type LiveEventsEventTypeDialogContainerProps = LiveEventsEventTypeDialogParams & {
  /** Optional children to render */
  children?: ReactNode
}

/** Default screenshot index for the dialog */
const initialScreenshotIndex = 0

/**
 * Container component for the Live Events Event Type Dialog.
 *
 * This component manages the state and interactions for displaying a dialog that shows
 * information about a specific live event type. It handles navigation between different
 * tabs, switching between tracked games, and transitioning to individual event dialogs.
 *
 * The component integrates with the route modal system to manage modal state and
 * provides a unified interface for viewing live event type information across different
 * tracked games.
 *
 * @param liveEventTypeId - The ID of the live event type to display
 * @param trackedGameId - The ID of the tracked game associated with the event type
 * @param tab - The active tab to display (defaults to AllEvents)
 * @param children - Optional children to render
 *
 * @returns A LiveEventsEventDialog component with configured data and handlers
 */
export const LiveEventsEventTypeDialogContainer: FC<LiveEventsEventTypeDialogContainerProps> = ({
  liveEventTypeId: eventTypeId,
  trackedGameId,
  tab = EventDialogTab.AllEvents,
  liveEventId: eventId,
  children,
}) => {
  const { popModal, replaceModal } = useRouteModal()
  const trackedGames = useTrackedGames({})
  const trackedEvents = useDefaultLiveEvents({ trackedGameIds: [trackedGameId] })
  const trackedGame = trackedGames.data?.find((trackedGame) => trackedGame.game.id === trackedGameId)
  const highlightedEventId = useAppSelector(selectHighlightedEvent)

  /**
   * Handles tab changes within the dialog.
   *
   * When a user switches tabs, this function updates the modal parameters
   * to reflect the new tab while maintaining the current event type and game context.
   *
   * @param tab - The new tab to switch to
   */
  const handleTabChange = useCallback(
    (tab: EventDialogTab) => {
      replaceModal({
        type: EModalType.LIVE_EVENT_TYPE,
        params: {
          trackedGameId: trackedGameId,
          liveEventTypeId: eventTypeId,
          tab: tab,
          liveEventId: eventId,
        },
      })
    },
    [replaceModal, trackedGameId, eventTypeId, eventId]
  )

  /**
   * Handles selection of a specific event from the event type.
   *
   * When a user selects a specific event, this function transitions the modal
   * from the event type view to the individual event view, setting the appropriate
   * parameters for the LIVE_EVENT modal type.
   *
   * @param event - The selected tracking event
   */
  const handleEventChange = useCallback(
    (event: TrackingEvent) => {
      replaceModal({
        type: EModalType.LIVE_EVENT,
        params: {
          trackedGameId: trackedGameId,
          liveEventTypeId: event.typeId,
          liveEventId: event.eventId,
          durationStart: event.start,
          durationEnd: event.end,
          tab: EventDialogTab.Description,
          screenshotIndex: initialScreenshotIndex,
        },
      })
    },
    [replaceModal, trackedGameId]
  )

  /**
   * Handles changes to the tracked game selection.
   *
   * When a user switches to a different tracked game, this function updates
   * the modal to show the same event type for the new game, resetting to the
   * AllEvents tab for consistency.
   *
   * @param trackedGame - The newly selected tracked game
   */
  const handleTrackedGameChange = useCallback(
    (trackedGame: TrackedGame) => {
      replaceModal({
        type: EModalType.LIVE_EVENT_TYPE,
        params: {
          trackedGameId: trackedGame.game.id,
          liveEventTypeId: eventTypeId,
          tab: EventDialogTab.AllEvents,
        },
      })
    },
    [eventTypeId, replaceModal]
  )

  /**
   * Handles screenshot index changes.
   *
   * This function is provided for interface consistency but should not be called
   * in the event type dialog context. It's handled gracefully as a no-op.
   *
   * @param index - The new screenshot index (unused in this context)
   */
  const handleScreenshotIndexChange = useCallback((index: number) => {
    // This shouldn't be called in the event type dialog, but handle it gracefully
  }, [])

  /**
   * Handles closing the dialog.
   *
   * Removes the current modal from the modal stack, effectively closing the dialog.
   */
  const handleClose = () => {
    popModal()
  }

  return (
    <LiveEventsEventDialog
      data={{
        eventId: eventId, // Optional event ID for loading archetype data in Evolution tab
        eventTypeId: eventTypeId,
        trackedGame: trackedGame,
        tab: tab,
        highlightedEventId: highlightedEventId,
        screenshotIndex: initialScreenshotIndex,
        trackedEvents: trackedEvents.events,
        trackedGames: trackedGames.data,
      }}
      open
      onClose={handleClose}
      onEventChange={handleEventChange}
      onTabChange={handleTabChange}
      onTrackedGameChange={handleTrackedGameChange}
      onScreenshotIndexChange={handleScreenshotIndexChange}
    />
  )
}
